import { useCallback, useRef } from "react";

import { Trans, useLingui } from "@lingui/react/macro";
import { apiFetch } from "@pontimed/api-client";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Outlet, useMatches, createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  Bell,
  Calendar,
  Database,
  Link2,
  Shield,
  User as UserIcon,
  type LucideIcon,
} from "lucide-react";

import { AccountSecurityBanner } from "@/components/account/account-security-banner";
import { DataTabContent, type DataSubTab } from "@/components/account/data/data-tab-content";
import { LinkedAccountsCard } from "@/components/account/linked-accounts-card";
import { NotificationSettingsClient } from "@/components/account/notification-settings-client";
import { PatientCalendarSync } from "@/components/account/patient-calendar-sync";
import { ProfileEditForm, type ProfileFormData } from "@/components/account/profile-edit-form";
import { SecurityTabContent } from "@/components/account/security/security-tab-content";
import { SettingsDangerZone } from "@/components/account/settings-danger-zone";
import { CookiePreferencesCard } from "@/components/consent/cookie-preferences-card";
import { PageHeader } from "@/components/layout/page-header";
import { MarketingConsentSection } from "@/components/consent/marketing-consent-section";
import { LoadErrorState } from "@/components/ui/load-error-state";
import { Skeleton } from "@/components/ui/skeleton";
import {
  SwipeableTabsContent,
  type SwipeableTabsHandle,
} from "@/components/ui/swipeable-tabs-content";
import { TabBar, type TabItem } from "@/components/ui/tab-bar";

type SettingsTab = "profile" | "notifications" | "accounts" | "calendar" | "security" | "data";
type SecuritySubTab = "personal" | "professional";
type AnySubTab = SecuritySubTab | DataSubTab;

const VALID_TABS: readonly SettingsTab[] = [
  "profile",
  "notifications",
  "accounts",
  "calendar",
  "security",
  "data",
] as const;

const VALID_SUBS: readonly AnySubTab[] = [
  "personal",
  "professional",
  "activity",
  "export",
] as const;

interface SettingsSearch {
  tab: SettingsTab;
  /** Subtab name. Validity depends on the active outer tab — security uses
   *  personal/professional, data uses activity/export. */
  sub?: AnySubTab;
  forceEnroll?: 1;
  /**
   * Why the user was forced into the enrollment screen. `workspace` =
   * provider/facility 2FA gate fired (NIS2 compliance for medical data).
   * Drives the banner copy on the security subtab.
   */
  reason?: "workspace";
  /** Where to bounce back after a successful enrollment. */
  next?: string;
  /** Back from connecting a provider (apps/api auth-oauth.ts buildSettingsLinkUrl). */
  linkProvider?: "google" | "apple" | "facebook";
  /** "ok" or a refusal code — LinkedAccountsCard shows it, then drops both. */
  linkResult?: string;
}

interface SettingsData {
  profileData: ProfileFormData;
  ownedProviders: { providerId: string; providerName: string }[];
  ownedBusinesses: { businessId: string; businessName: string }[];
  facilityTeams: { facilityId: string; facilityName: string; role: string }[];
  isPro: boolean;
}

const SETTINGS_KEY = ["api", "/api/account/user/settings-data"] as const;

const settingsQueryOptions = {
  queryKey: SETTINGS_KEY,
  queryFn: () => apiFetch.get<SettingsData>("/api/account/user/settings-data"),
} as const;

export const Route = createFileRoute("/_authenticated/account/user/settings")({
  validateSearch: (search: Record<string, unknown>): SettingsSearch => {
    const rawTab = typeof search.tab === "string" ? search.tab : "";
    const rawSub = typeof search.sub === "string" ? search.sub : "";
    const rawForce = search.forceEnroll === 1 || search.forceEnroll === "1";
    const rawReason = typeof search.reason === "string" ? search.reason : "";
    const rawNext = typeof search.next === "string" ? search.next : "";
    const rawLinkProvider = search.linkProvider;
    const rawLinkResult = typeof search.linkResult === "string" ? search.linkResult : "";
    return {
      tab: VALID_TABS.includes(rawTab as SettingsTab) ? (rawTab as SettingsTab) : "profile",
      sub: VALID_SUBS.includes(rawSub as AnySubTab) ? (rawSub as AnySubTab) : undefined,
      forceEnroll: rawForce ? 1 : undefined,
      reason: rawReason === "workspace" ? "workspace" : undefined,
      next: rawNext || undefined,
      linkProvider:
        rawLinkProvider === "google" ||
        rawLinkProvider === "apple" ||
        rawLinkProvider === "facebook"
          ? rawLinkProvider
          : undefined,
      linkResult: rawLinkResult || undefined,
    };
  },
  // Prefetch settings-data in parallel with the route chunk download so the
  // component reads it from cache on mount instead of showing a skeleton
  // while the request fires.
  loader: ({ context }) => context.queryClient.ensureQueryData(settingsQueryOptions),
  component: SettingsRouteShell,
});

// Child routes (merge, profile, calendar, notifications) are standalone
// pages, mirroring legacy Next.js where each was its own page — when one
// matches, render it instead of the settings hub.
function SettingsRouteShell() {
  const matches = useMatches();
  const hasChild = matches.some((m) => m.routeId.startsWith(`${Route.id}/`));
  if (hasChild) return <Outlet />;
  return <SettingsPage />;
}

function SettingsPage() {
  const { t } = useLingui();
  const { tab: activeTab, sub: rawSub, forceEnroll, reason } = Route.useSearch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const swipeRef = useRef<SwipeableTabsHandle>(null);

  const { data, isLoading, isError, refetch } = useQuery(settingsQueryOptions);

  const setTab = useCallback(
    (next: SettingsTab) => {
      navigate({ to: "/account/user/settings", search: (prev) => ({ ...prev, tab: next }) });
    },
    [navigate],
  );

  const setSub = useCallback(
    (next: SecuritySubTab) => {
      navigate({
        to: "/account/user/settings",
        search: (prev) => ({ ...prev, tab: "security", sub: next }),
        replace: true,
      });
    },
    [navigate],
  );

  const setDataSub = useCallback(
    (next: DataSubTab) => {
      navigate({
        to: "/account/user/settings",
        search: (prev) => ({ ...prev, tab: "data", sub: next }),
        replace: true,
      });
    },
    [navigate],
  );

  // Subtab overscroll handoff: when user swipes past the first/last security
  // subtab, switch the OUTER settings tab so the gesture flows naturally.
  const handleSubtabOverscroll = useCallback(
    (direction: "next" | "prev") => {
      const idx = VALID_TABS.indexOf(activeTab);
      if (direction === "next" && idx < VALID_TABS.length - 1) {
        setTab(VALID_TABS[idx + 1]);
      } else if (direction === "prev" && idx > 0) {
        setTab(VALID_TABS[idx - 1]);
      }
    },
    [activeTab, setTab],
  );

  const tabs: { id: SettingsTab; label: React.ReactNode; icon: LucideIcon; soon?: boolean }[] = [
    { id: "profile", label: <Trans>Profile</Trans>, icon: UserIcon },
    { id: "notifications", label: <Trans>Notifications</Trans>, icon: Bell },
    { id: "accounts", label: <Trans>Accounts</Trans>, icon: Link2 },
    { id: "calendar", label: <Trans>Calendar</Trans>, icon: Calendar },
    { id: "security", label: <Trans>Security</Trans>, icon: Shield },
    { id: "data", label: <Trans>Data</Trans>, icon: Database },
  ];

  if (isLoading) return <SettingsSkeleton />;
  if (isError || !data) return <ErrorState onRetry={() => refetch()} />;

  const { profileData, ownedProviders, ownedBusinesses, facilityTeams, isPro } = data;

  // Resolve initial security subtab: explicit sub wins, then forceEnroll → professional
  // (only if user is pro), else fall back to personal.
  const activeSub: SecuritySubTab =
    rawSub === "professional" && isPro
      ? "professional"
      : rawSub === "personal"
        ? "personal"
        : forceEnroll && isPro
          ? "professional"
          : "personal";

  const activeDataSub: DataSubTab = rawSub === "export" ? "export" : "activity";

  return (
    <div>
      <PageHeader
        title={<Trans>Settings</Trans>}
        description={
          <Trans>Profile, notifications, linked accounts, calendar, and security.</Trans>
        }
      />

      <TabBar
        tabs={tabs.map((tab): TabItem<SettingsTab> => {
          const Icon = tab.icon;
          return {
            id: tab.id,
            label: tab.label,
            icon: <Icon className="size-4" />,
            trailing: tab.soon ? (
              <span className="rounded bg-amber-500/10 px-1 py-0.5 text-[0.5rem] leading-none font-semibold text-amber-600 uppercase dark:text-amber-400">
                {t`Soon`}
              </span>
            ) : undefined,
          };
        })}
        activeTab={activeTab}
        onTabChange={setTab}
        onActiveTabClick={() => swipeRef.current?.scrollActiveToTop()}
        className="mt-6"
      />

      <SwipeableTabsContent
        ref={swipeRef}
        activeTab={activeTab}
        tabIds={VALID_TABS}
        onTabChange={setTab}
        className="mt-4"
        // Security + Data both host their own subtab carousels — disable the
        // outer gesture while either is active so the inner carousel wins.
        disabled={activeTab === "security" || activeTab === "data"}
        renderPanel={(id) => {
          if (id === "security") {
            // Security owns its own banner per-subtab via SecurityTabContent.
            return (
              <SecurityTabContent
                isPro={isPro}
                forceEnroll={!!forceEnroll}
                forceEnrollReason={reason}
                activeSub={activeSub}
                setSub={setSub}
                onSubtabOverscroll={handleSubtabOverscroll}
              />
            );
          }
          if (id === "data") {
            return (
              <DataTabContent
                activeSub={activeDataSub}
                setSub={setDataSub}
                onSubtabOverscroll={handleSubtabOverscroll}
              />
            );
          }

          let inner: React.ReactNode = null;
          if (id === "profile") {
            inner = (
              <ProfileEditForm
                initialData={profileData}
                onSaved={() => queryClient.invalidateQueries({ queryKey: SETTINGS_KEY })}
              />
            );
          } else if (id === "notifications") {
            // The full surface: channel matrix + promotional-message consent
            // (opt in / withdraw at any time, Art. 7(3)) + cookie choices.
            inner = (
              <div className="space-y-6">
                <NotificationSettingsClient />
                <MarketingConsentSection />
                <CookiePreferencesCard />
              </div>
            );
          } else if (id === "accounts") {
            inner = (
              <div className="space-y-6">
                <LinkedAccountsCard />
                <SettingsDangerZone
                  ownedProviders={ownedProviders}
                  ownedBusinesses={ownedBusinesses}
                  facilityTeams={facilityTeams}
                />
              </div>
            );
          } else if (id === "calendar") {
            inner = <PatientCalendarSync />;
          }

          if (!inner) return null;
          return (
            <div className="space-y-6">
              {inner}
              <AccountSecurityBanner />
            </div>
          );
        }}
      />
    </div>
  );
}

function SettingsSkeleton() {
  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <Skeleton className="h-8 w-32" />
          <Skeleton className="mt-2 h-4 w-64" />
        </div>
      </div>
      <div className="mt-6 flex gap-2 overflow-hidden">
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} className="h-9 w-24 shrink-0 rounded-lg" />
        ))}
      </div>
      <div className="mt-4 space-y-4">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    </div>
  );
}

function ErrorState({ onRetry }: { onRetry: () => void }) {
  return (
    <LoadErrorState
      className="mt-2"
      title={<Trans>Could not load settings.</Trans>}
      onRetry={onRetry}
    />
  );
}
